<template>
  <v-container 
  v-if="isDataReady"
  fluid>
    <section-title-with-request-id
    :request-id="partRequestProp.requestId"
    title="pages.partRequest.repairRequestDetails"
    :subtitle="$parent.isRole(['ADMIN', 'SUPER_ADMIN', 'SUPERVISOR']) ? 'pages.partRequest.pleaseApproveOrRejectDetailTab' : null"
    subtitle-bold
    class="mb-3"/>
    
    <updatable-status-by-admin
    :data="partRequestProp"
    :loading="isLoading"
    :statusesFetcher="getPartRequestStatuses"
    :statusGetter="getStatusAsObject"/>
    <v-row dense class="mt-10">
      <v-col>
        <span v-if="$parent.isRole(['ADMIN', 'SUPER_ADMIN', 'SUPERVISOR'])" class="process-repair-tab-section-title">{{$t('pages.repair.partsForApproval')}}</span>
        <span v-else class="process-repair-tab-section-title">{{$t('pages.repair.partsInfo')}}</span>
      </v-col>
      <v-col
      v-if="$parent.isRole(['ADMIN', 'SUPER_ADMIN', 'SUPERVISOR'])"
      class="d-flex justify-end">
        <approval-comment-dialog-trigger
        @click="openApprovalCommentDialogForRequest"
        :value="newData.approvalComments['INITIAL']"
        :disable-on-empty="disabled"/>
      </v-col>
    </v-row>
    <v-row dense>
      <v-col>
        <approval-comment-dialog
        v-if="approvalCommentDialog.model"
        v-model="approvalCommentDialog.model"
        :comment="approvalCommentDialog.comment"
        :status="approvalCommentDialog.status"
        :step-status="partRequestProp.status.key"
        :owner-id="approvalCommentDialog.ownerId"
        :saver="approvalCommentDialog.saver"
        @success="onApprovalCommentSaved"
        :preview="disabled"/>
        <main-data-table
        :headers="getHeaders"
        :items="newData.requestedParts"
        number-pages
        :mark-items="$parent.isRole(['ADMIN', 'SUPER_ADMIN', 'SUPERVISOR']) && !disabled"
        set-image
        :with-search="false"
        :disabled="disabled"
        :page.sync="page"
        :size="-1"
        :items-per-page-options="[-1]"
        :total-elements="-1"
        hide-default-footer>
          <template v-slot:[`item.initialApproval`]="{ item }">
            <waiting-approval-label
            :status="item.initialApproval"/>
          </template>
          <template
          v-if="$parent.isRole(['ADMIN', 'SUPER_ADMIN', 'SUPERVISOR'])"
          v-slot:[`item.approval`]="{ item }">
            <waiting-approval-btns
            :current-status="item.initialApproval"
            :loading="isLoading"
            :disabled="disabled"
            @selected="(status) => changeInitialStatusSingle(item, status)"/>
          </template>
          <template
          v-if="$parent.isRole(['ADMIN', 'SUPER_ADMIN', 'SUPERVISOR'])"
          v-slot:[`item.approvalComments`]="{ item }">
            <approval-comment-dialog-trigger
            @click="openApprovalCommentDialogForItem(item)"
            :value="item.approvalComments['INITIAL']"
            :disable-on-empty="disabled"/>
          </template>
        </main-data-table>

        <v-row 
        v-if="$parent.isRole(['ADMIN', 'SUPER_ADMIN', 'SUPERVISOR'])"
        class="mt-5"
        dense>
          <v-col>
            <process-repair-quick-btns
            :config="getQuickBtnsConfig"
            :loading.sync="isLoading"
            title="common.pressContinueWhenReady"
            on-success-repair-status-message="dialogs.messages.success.firstDetailsCompleted"/>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
    <export-xlsx-row
    :width="150"
    :height="50"
    :disabled="partRequestProp.requestedParts.length === 0"
    :loading.sync="isLoading"
    class="px-4 mt-4"
    name="export.title.partRequestDetailTab"
    :exporter="getExporter"/>
  </v-container>
</template>
<script>
import ProcessRepairQuickBtns from '@/components/process-repair-tabs/ProcessRepairQuickBtns'
import MainDataTable from '@/components/common/MainDataTable'
import UpdatableStatusByAdmin from '@/components/common/process-tabs/UpdatableStatusByAdmin'
import WaitingApprovalLabel from '@/components/common/process-tabs/WaitingApprovalLabel'
import WaitingApprovalBtns from '@/components/common/process-tabs/WaitingApprovalBtns'
import ExportXlsxRow from '@/components/common/ExportXlsxRow'
import ApprovalCommentDialog from '@/components/dialogs/ApprovalCommentDialog'
import ApprovalCommentDialogTrigger from '@/components/common/triggers/ApprovalCommentDialogTrigger'
import SectionTitleWithRequestId from '@/components/common/process-tabs/shared/SectionTitleWithRequestId'
//ENABLE_CHANGE_DIALOG - This method is needed for changes dialog.
// getTextByKeyInitialStatus
import { getObjectPartRequestStatusByKey, getAvailablePartRequestStatuses,  } from '@/utils/statusesUtil'
import { updatePartRequestItemsInitialApproval } from '@/repositories/partRequestRepo'
import { execHttpRequestThrowable } from '@/services/http'
import { exportPartRequestDetailsTabXlsx } from '@/repositories/exportRepo'
//ENABLE_CHANGE_DIALOG - This method is needed for changes dialog.
// compareArrays
import { copyObj } from '@/utils/StringUtil'
import { saveRequestApprovalComment, saveItemApprovalComment } from "@/repositories/partRequestRepo"
export default {
  name: 'DetailsTabPartRequest',
  components: {
    ProcessRepairQuickBtns,
    MainDataTable,
    UpdatableStatusByAdmin,
    WaitingApprovalLabel,
    WaitingApprovalBtns,
    ExportXlsxRow,
    ApprovalCommentDialog,
    ApprovalCommentDialogTrigger,
    SectionTitleWithRequestId
  },
  props: {
    data: {
      type: Object,
      required: true
    },
    disabled: Boolean
  },
  data() {
    return {
      headers: [{
          text: 'headers.category',
          align: 'start',
          sortable: true,
          value: 'category'
        }, {
          text: 'headers.partName',
          align: 'start',
          sortable: true,
          value: 'partName'
        }, {
          text: 'headers.quantity',
          align: 'start',
          sortable: true,
          value: 'quantity'
        }, {
          text: 'headers.initialApproval',
          align: 'start',
          sortable: true,
          value: 'initialApproval'
        }, {
          text: 'headers.approval',
          align: 'start',
          sortable: true,
          value: 'approval'
        }, {
          text: 'headers.approvalComment',
          align: 'start',
          sortable: true,
          value: 'approvalComments'
        }
      ],
      page: 1,
      newData: null,
      isLoading: false,
      isDataReady: false,
      approvalCommentDialog: {
        model: false,
        comment: null,
        status: null,
        ownerId: -1,
        isItem: false,
        saver: null
      }
    }
  },
  computed: {
    partRequestProp: {
      get: function() {
        return this.$props.data
      },
      set: function(v) {
        this.$emit('update:data', v)
      }
    },
    getHeaders() {
      const headers = []
      const hideHeaders = ["mark", "approval", "approvalComments"]

      for (const h of this.headers) {
        if (this.$parent.isRole(['SERVICE', 'CLIENT', 'SUSTAINABILITY_MANAGER'])) {
          if (hideHeaders.includes(h.value)) continue
        }

        headers.push(h)
      }

      return headers
    },
    isMarkedApprovedBtnDisable() {
      
      return this.newData.requestedParts.filter(p => p.mark === true).length === 0
    },
    getQuickBtnsConfig() {
      return [
        {
          name: 'RejectAll',
          events: {
            'click': () => this.setChangedDialogItems('REJECTED', false)
          },
          props: {
            'disabled': this.disabled
          }
        }, {
          name: 'ApproveAll',
          events: {
            'click': () => this.setChangedDialogItems('APPROVED', false)
          },
          props: {
            'disabled': this.disabled
          }
        }, {
          name: 'ApproveMarked',
          events: {
            'click': () => this.setChangedDialogItems('APPROVED', true)
          },
          props: {
            'disabled': this.disabled || this.isMarkedApprovedBtnDisable
          }
        }, {
          name: 'Continue',
          props: {
            'disabled': this.disabled || !this.newData.isAllowedToContinue
          }
        }
      ]
    },
    getExporter() {
      return async () => await exportPartRequestDetailsTabXlsx(this.$route.params.id)
    }
  },
  methods: {
    async changeInitialStatusSingle(part, status) {
      await this.tryToChangeInitialStatus([{
        id: part.id,
        initialApproval: status
      }], false)
    },
    async tryToChangeInitialStatus(parts, showSuccessDialog) {
      const data = parts.map(p => {
        return {
          id: p.id,
          initialApproval: p.initialApproval
        }
      })
      const status = data[0].initialApproval

      const messages = {
        ignoreSuccessDialog: !showSuccessDialog,
        _200: this.$t(status === 'APPROVED' ? 'dialogs.messages.success.approvalDecisionSaved' : 'dialogs.messages.success.rejectionDecisionSaved')
      }
      const r = await execHttpRequestThrowable(async () => await updatePartRequestItemsInitialApproval(this.$route.params.id, data), messages, v => this.isLoading = v)
      const ids = parts.map(p => p.id)
      //Update server parts
      this.partRequestProp.requestedParts
        .filter(p => ids.includes(p.id))
        .forEach(p => p.initialApproval = String(status))
      //Update local parts
      this.newData.requestedParts
        .filter(p => ids.includes(p.id))
        .forEach(p => p.initialApproval = String(status))

      this.newData.isAllowedToContinue = r.response.data
    },
    setChangedDialogItems(status, isMarkBtn) {
      const filteredParts = this.newData.requestedParts.filter(p => (isMarkBtn && p.mark && p.initialApproval !== status) || (!isMarkBtn && p.initialApproval !== status))

      if (filteredParts.length === 0) return
      this.tryToChangeInitialStatus(filteredParts.map(p => {
        return {
          id: p.id,
          initialApproval: status
        }
      }))
      // NOTE: ENABLE_CHANGE_DIALOG - Enable this code, if changes dialog is needed.
      //-----------------------------------------------------------------
      // const newParts = filteredParts.map(p => {
      //   return {
      //     rowNumber: p.rowNumber,
      //     initialApproval: status
      //   }
      // })
      // const oldParts = filteredParts.map(p => {
      //   return {
      //     rowNumber: p.rowNumber,
      //     initialApproval: p.initialApproval
      //   }
      // })

      // const props = [
      //   {
      //     field: 'textFields.approval',
      //     prop: 'initialApproval',
      //     getter: getTextByKeyInitialStatus
      //   }
      // ]

      // this.$changesDialog.open(
      //   'dialogs.titles.changes',
      //   compareArrays(oldParts, newParts, props),
      //   async () => await this.tryToChangeInitialStatus(filteredParts.map(p => {
      //     return {
      //       id: p.id,
      //       initialApproval: status
      //     }
      //   }), true),
      //   true
      // )
      //-----------------------------------------------------------------
    },
    openApprovalCommentDialogForItem({approvalComments, initialApproval, id}) {
      this.approvalCommentDialog = {
        model: true,
        comment: approvalComments['INITIAL'],
        status: initialApproval,
        ownerId: id,
        isItem: true,
        saver: saveItemApprovalComment
      }
    },
    openApprovalCommentDialogForRequest() {
      this.approvalCommentDialog = {
        model: true,
        comment: this.newData.approvalComments['INITIAL'],
        status: "REQUEST",
        ownerId: Number(this.$route.params.id),
        isItem: false,
        saver: saveRequestApprovalComment
      }
    },
    onApprovalCommentSaved({ownerId, comment}) {
      // NOTE: for part
      if (this.approvalCommentDialog.isItem) {
        //Update server parts
        this.partRequestProp.requestedParts
          .filter(p => p.id === ownerId)
          .forEach(p => p.approvalComments['INITIAL'] = String(comment))
        //Update local parts
        this.newData.requestedParts
          .filter(p => p.id === ownerId)
          .forEach(p => p.approvalComments['INITIAL'] = String(comment))
      }
      // NOTE: for whole request
      else {
        //Update server parts
        this.partRequestProp.approvalComments["INITIAL"] = comment
        //Update local parts
        this.newData.approvalComments["INITIAL"] = comment
      }

      this.approvalCommentDialog.model = false
    },
    getPartRequestStatuses() {
      return getAvailablePartRequestStatuses()
    },
    getStatusAsObject(s) {
      return getObjectPartRequestStatusByKey(s)
    }
  },
  mounted() {
    // console.log(this.partRequestProp.requestedParts)
    this.newData = {
      status: this.partRequestProp.status,
      approvalComments: copyObj(this.partRequestProp.approvalComments),
      requestedParts: this.partRequestProp.requestedParts.map((p, i) => {
        const newPart = copyObj(p)
        newPart.rowNumber = i + 1
        return newPart
      }),
      isAllowedToContinue: this.partRequestProp.isAllowedToContinue
    }
    this.isDataReady = true
  }
}
</script>
<style>

</style>